import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import AdminSidebar from './components/admin/AdminSidebar';

// User Pages
import HomePage from './pages/user/HomePage';
import PropertyListingPage from './pages/user/PropertyListingPage';
import PropertyDetailsPage from './pages/user/PropertyDetailsPage';
import AboutPage from './pages/user/AboutPage';
import ContactPage from './pages/user/ContactPage';
import PrivacyPolicyPage from './pages/user/PrivacyPolicyPage';
import LoginPage from './pages/user/LoginPage';
import RegisterPage from './pages/user/RegisterPage';
import CarrierPage from './pages/user/CarrierPage';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import ManagePropertiesPage from './pages/admin/ManagePropertiesPage';
import ManageCategoriesPage from './pages/admin/ManageCategoriesPage';
import ManageUsersPage from './pages/admin/ManageUsersPage';
import ManageInquiriesPage from './pages/admin/ManageInquiriesPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const user = sessionStorage.getItem('user');

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Admin Protected Route with Auto-Redirect
const AdminRoute = ({ children }) => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');

  useEffect(() => {
    // Allow full admins or subadmins that have at least one admin permission
    const allowed = user && (user.role === 'admin' || (user.role === 'subadmin' && Array.isArray(user.permissions) && user.permissions.length > 0));
    if (!allowed) {
      navigate('/', { replace: true });
    }
    setIsChecking(false);
  }, [user, navigate]);

  if (isChecking) return null;

  const allowed = user && (user.role === 'admin' || (user.role === 'subadmin' && Array.isArray(user.permissions) && user.permissions.length > 0));
  if (!allowed) return <Navigate to="/" replace />;

  return children;
};

// Route wrapper to require a specific permission (admins bypass)
const PermissionRoute = ({ permission, children }) => {
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');

  if (user.role === 'admin') return children;

  if (user.role === 'subadmin') {
    // If no specific permission requested, allow any subadmin with at least one permission
    if (!permission) {
      return Array.isArray(user.permissions) && user.permissions.length > 0 ? children : <Navigate to="/" replace />;
    }
    return Array.isArray(user.permissions) && user.permissions.includes(permission) ? children : <Navigate to="/" replace />;
  }

  return <Navigate to="/" replace />;
};

function App() {
  const [user, setUser] = useState(() => {
    const userData = sessionStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  });
  const [, setAuthChange] = useState(0); // Trigger re-render on auth changes

  useEffect(() => {
    const handleAuthChange = () => {
      const userData = sessionStorage.getItem('user');
      setUser(userData ? JSON.parse(userData) : null);
      setAuthChange(prev => prev + 1); // Force re-render
    };

    // Listen for custom auth change event
    window.addEventListener('authChange', handleAuthChange);
    // Also listen for storage changes from other tabs
    window.addEventListener('storage', handleAuthChange);
    
    return () => {
      window.removeEventListener('authChange', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, []);

  const isAdmin = window.location.pathname.startsWith('/admin');
  const isUserAdmin = user && (user.role === 'admin' || (user.role === 'subadmin' && Array.isArray(user.permissions) && user.permissions.length > 0));

  return (
    <Router>
      <div className="flex min-h-screen flex-col">
        {/* Admin Layout */}
        {isAdmin && isUserAdmin ? (
          <div className="flex flex-1">
            <AdminSidebar />
            <div className="flex-1 ml-64">
              <Routes>
                <Route path="/admin/dashboard" element={
                  <AdminRoute>
                    <PermissionRoute>
                      <AdminDashboardPage />
                    </PermissionRoute>
                  </AdminRoute>
                } />

                <Route path="/admin/properties" element={
                  <AdminRoute>
                    <PermissionRoute permission="manage_properties">
                      <ManagePropertiesPage />
                    </PermissionRoute>
                  </AdminRoute>
                } />

                <Route path="/admin/categories" element={
                  <AdminRoute>
                    <PermissionRoute permission="manage_categories">
                      <ManageCategoriesPage />
                    </PermissionRoute>
                  </AdminRoute>
                } />

                <Route path="/admin/users" element={
                  <AdminRoute>
                    <PermissionRoute permission="manage_users">
                      <ManageUsersPage />
                    </PermissionRoute>
                  </AdminRoute>
                } />

                <Route path="/admin/inquiries" element={
                  <AdminRoute>
                    <PermissionRoute permission="view_inquiries">
                      <ManageInquiriesPage />
                    </PermissionRoute>
                  </AdminRoute>
                } />
              </Routes>
            </div>
          </div>
        ) : (
          // User Layout
          <>
            <Navbar />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/properties" element={<PropertyListingPage />} />
                <Route path="/property/:id" element={<PropertyDetailsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/carrier" element={<CarrierPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                {/* Redirect admin routes to home if not admin */}
                <Route path="/admin/*" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </main>
            <Footer />
          </>
        )}
      </div>
    </Router>
  );
}

export default App;
